import { uniq } from "lodash";
import { InvestmentTypeEnum } from "@rentablo/api/apiv1";
import { Image } from "@providers";

export type Security = {
  isin?: string | undefined;
  ticker_symbol?: string | undefined;
  tickerSymbol?: string | undefined;
  quoteProvider?: string | undefined;
  quote?: {
    quoteProvider?: string;
  };
  hasValidIsin?: boolean;
  type?: string;
};

export type QuoteProviderGroup = Record<string, Security[] | undefined>;
export type LogoResult = { id: string; url: string };

export const ALLOWED_TYPES: InvestmentTypeEnum[] = [
  InvestmentTypeEnum._11_STOCK,
  InvestmentTypeEnum._22_ETF,
  InvestmentTypeEnum._61_PMETALS,
];

export const isCryptoProvider = (quoteProvider: string) =>
  quoteProvider === "crypto";

export const getIsinsToFetch = (
  data: Security[],
  images: Record<string, Image>
): string[] =>
  uniq(
    data
      .filter(
        (security) =>
          security.hasValidIsin &&
          security.isin &&
          ALLOWED_TYPES.includes(security.type as InvestmentTypeEnum) &&
          !((security.isin as string) in images) &&
          !images[security.isin]?.isFetching
      )
      .map((security) => security.isin as string)
  );

export const getSymbolsToFetch = (
  data: Security[],
  images: Record<string, Image>
): string[] =>
  uniq(
    data.map((security) => security.ticker_symbol || security.tickerSymbol)
  ).filter(
    (symbol): symbol is string =>
      Boolean(symbol) &&
      !((symbol as string) in images) &&
      !images[symbol as string]?.isFetching
  );

export const getIdsToFetchForProvider = (
  quoteProvider: string,
  group: QuoteProviderGroup,
  images: Record<string, Image>
): string[] => {
  const items = group[quoteProvider] ?? [];
  return isCryptoProvider(quoteProvider)
    ? getSymbolsToFetch(items, images)
    : getIsinsToFetch(items, images);
};

export const markFetching = (
  prev: Record<string, Image>,
  ids: string[]
): Record<string, Image> =>
  ids.reduce(
    (acc, id) => {
      acc[id] = { isFetching: true, value: prev[id]?.value };
      return acc;
    },
    {} as Record<string, Image>
  );

export const markResolved = (idsWithUrl: LogoResult[]): Record<string, Image> =>
  idsWithUrl.reduce(
    (acc, { id, url }) => {
      acc[id] = { isFetching: false, value: url };
      return acc;
    },
    {} as Record<string, Image>
  );

export const markMissing = (ids: string[]): Record<string, Image> =>
  ids.reduce(
    (acc, id) => {
      acc[id] = { isFetching: false, value: undefined };
      return acc;
    },
    {} as Record<string, Image>
  );
